import { useId } from 'react';

type AppLogoProps = {
  variant?: 'full' | 'mark';
  height?: number;
  className?: string;
};

function logoClass(className?: string) {
  return ['app-logo', className].filter(Boolean).join(' ');
}

function BolitaBall({ size, gradId }: { size: number; gradId: string }) {
  const showPair = size >= 34;
  const mainNumber = showPair ? '08' : '8';
  const mainSize = showPair ? 7.2 : 8.2;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      aria-hidden
      className="app-logo__mark"
    >
      <defs>
        <radialGradient id={`${gradId}-ball`} cx="36%" cy="30%" r="68%">
          <stop offset="0%" stopColor="var(--logo-ball-hi)" />
          <stop offset="40%" stopColor="var(--logo-ball-mid)" />
          <stop offset="100%" stopColor="var(--logo-ball-lo)" />
        </radialGradient>
        <radialGradient id={`${gradId}-shine`} cx="28%" cy="22%" r="32%">
          <stop offset="0%" stopColor="var(--logo-shine)" stopOpacity="0.42" />
          <stop offset="100%" stopColor="var(--logo-shine)" stopOpacity="0" />
        </radialGradient>
      </defs>

      <circle cx="16" cy="16" r="14.5" fill={`url(#${gradId}-ball)`} />
      <circle cx="16" cy="16" r="14.1" fill="none" stroke="var(--color-terra)" strokeWidth="0.7" opacity="0.5" />
      <ellipse cx="10.2" cy="9.2" rx="4.4" ry="3.1" fill={`url(#${gradId}-shine)`} />

      <circle cx="16" cy="16" r="6.5" fill="var(--logo-disc)" />
      <circle cx="16" cy="16" r="6.5" fill="none" stroke="var(--color-terra)" strokeWidth="1.15" />
      <text
        x="16"
        y="19.1"
        textAnchor="middle"
        fill="var(--color-teal)"
        fontFamily="ui-monospace, 'Cascadia Mono', monospace"
        fontSize={mainSize}
        fontWeight="800"
        letterSpacing={showPair ? '-0.08em' : '0'}
      >
        {mainNumber}
      </text>

      <circle cx="25.8" cy="7.8" r="4.1" fill="var(--color-teal)" />
      <circle cx="25.8" cy="7.8" r="3.8" fill="none" stroke="var(--color-teal-light)" strokeWidth="0.5" opacity="0.6" />
      <circle cx="25.8" cy="7.8" r="2.3" fill="var(--logo-disc)" />
      <text
        x="25.8"
        y="8.95"
        textAnchor="middle"
        fill="var(--color-teal)"
        fontFamily="ui-monospace, 'Cascadia Mono', monospace"
        fontSize="4.8"
        fontWeight="800"
      >
        7
      </text>
    </svg>
  );
}

function BolitaBallLarge({ gradId }: { gradId: string }) {
  return (
    <>
      <circle cx="16" cy="16" r="14.5" fill={`url(#${gradId}-ball)`} />
      <circle cx="16" cy="16" r="14.1" fill="none" stroke="var(--color-terra)" strokeWidth="0.7" opacity="0.5" />
      <ellipse cx="10.2" cy="9.2" rx="4.4" ry="3.1" fill={`url(#${gradId}-shine)`} />
      <circle cx="16" cy="16" r="6.5" fill="var(--logo-disc)" />
      <circle cx="16" cy="16" r="6.5" fill="none" stroke="var(--color-terra)" strokeWidth="1.15" />
      <text
        x="16"
        y="19.1"
        textAnchor="middle"
        fill="var(--color-teal)"
        fontFamily="ui-monospace, 'Cascadia Mono', monospace"
        fontSize="7.2"
        fontWeight="800"
        letterSpacing="-0.08em"
      >
        08
      </text>
      <circle cx="25.8" cy="7.8" r="4.1" fill="var(--color-teal)" />
      <circle cx="25.8" cy="7.8" r="2.3" fill="var(--logo-disc)" />
      <text
        x="25.8"
        y="8.95"
        textAnchor="middle"
        fill="var(--color-teal)"
        fontFamily="ui-monospace, 'Cascadia Mono', monospace"
        fontSize="4.8"
        fontWeight="800"
      >
        7
      </text>
    </>
  );
}

export function AppLogo({ variant = 'full', height = 32, className }: AppLogoProps) {
  const gradId = useId().replace(/:/g, '');

  if (variant === 'mark') {
    return (
      <div className={logoClass(className)} role="img" aria-label="Alea TUI">
        <BolitaBall size={height} gradId={gradId} />
      </div>
    );
  }

  const width = Math.round(height * 3.6);

  return (
    <svg
      className={logoClass(className)}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 120 32"
      width={width}
      height={height}
      role="img"
      aria-label="Alea TUI"
    >
      <defs>
        <radialGradient id={`${gradId}-ball`} cx="36%" cy="30%" r="68%">
          <stop offset="0%" stopColor="var(--logo-ball-hi)" />
          <stop offset="40%" stopColor="var(--logo-ball-mid)" />
          <stop offset="100%" stopColor="var(--logo-ball-lo)" />
        </radialGradient>
        <radialGradient id={`${gradId}-shine`} cx="28%" cy="22%" r="32%">
          <stop offset="0%" stopColor="var(--logo-shine)" stopOpacity="0.42" />
          <stop offset="100%" stopColor="var(--logo-shine)" stopOpacity="0" />
        </radialGradient>
      </defs>
      <BolitaBallLarge gradId={gradId} />
      <text
        x="38"
        y="14"
        fill="currentColor"
        fontFamily="system-ui, -apple-system, 'Segoe UI', sans-serif"
        fontSize="10"
        fontWeight="600"
        letterSpacing="0.2em"
      >
        ALEA TUI
      </text>
      <text
        x="38"
        y="26"
        fill="var(--color-teal)"
        fontFamily="Georgia, 'Times New Roman', serif"
        fontSize="11"
        fontStyle="italic"
      >
        bolita · 00—99
      </text>
    </svg>
  );
}

export function TaglineBadge({ t }: { t: Record<string, string>; p?: unknown }) {
  return (
    <div className="tagline-badge">
      <div className="tagline-badge__title">
        {t.heroTitleLead}{' '}
        <span className="tagline-badge__accent">{t.heroTitleAccent}</span>
      </div>
      <div className="tagline-badge__range">00 — 99</div>
    </div>
  );
}
